import * as React from 'react';
import { Loader2, Eye } from 'lucide-react';
import { useResolvedCombination } from '@/hooks/catalogue/use-resolved-combination';
import { useCalculatorPreview } from '@/hooks/calculator/use-calculator-preview';
import { CreateLineItemSchema } from '@cutlyft/zod-schemas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface DynamicLineItemFormProps {
  combinationId: number;
  materialCode: string;
  initialValues?: Record<string, any>;
  onSubmit: (values: Record<string, any>) => void;
  onReset: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
  isEdit?: boolean;
}

export function DynamicLineItemForm({
  combinationId,
  materialCode,
  initialValues = {},
  onSubmit,
  onReset,
  isSubmitting,
  submitLabel = 'Save',
  isEdit = false,
}: DynamicLineItemFormProps) {
  const { data: combination, isLoading: isComboLoading } = useResolvedCombination(combinationId);
  const previewMutation = useCalculatorPreview();
  const previewTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const [form, setForm] = React.useState<Record<string, any>>(() => {
    return {
      qty: 1,
      inputUnit: 'mm',
      ...initialValues,
    };
  });

  const setField = (field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Maps DB input_field codes to form state keys.
  // All DB-driven fields must have an entry here so their values land in the
  // correct key for submission and preview.
  const getFormKey = (code: string) => {
    if (code === 'HEIGHT')     return 'heightMm';
    if (code === 'WIDTH')      return 'widthMm';
    if (code === 'QTY')        return 'qty';
    if (code === 'GLASS_TYPE') return 'glassType';
    if (code === 'NOTES')      return 'locationNotes';
    if (code === 'G1')         return 'g1Value';
    if (code === 'G2')         return 'g2Value';
    return code;
  };

  React.useEffect(() => {
    if (!combination) return;

    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);

    const payload = {
      combinationId,
      seriesId: combination.seriesId,
      typeId: combination.typeId,
      material: materialCode,
      qty: parseInt(form.qty || 1, 10),
      inputUnit: form.inputUnit || 'mm',
      inputWidthValue: parseFloat(form.widthMm || 0),
      inputHeightValue: parseFloat(form.heightMm || 0),
      g1Value: form.g1Value ? parseFloat(form.g1Value) : undefined,
      g2Value: form.g2Value ? parseFloat(form.g2Value) : undefined,
      widthMm: parseInt(form.widthMm || 0, 10),
      heightMm: parseInt(form.heightMm || 0, 10),
    };

    const width = parseInt(form.widthMm, 10);
    const height = parseInt(form.heightMm, 10);
    if (!width || !height || width < 100 || height < 100) return;

    previewTimerRef.current = setTimeout(() => {
      // @ts-expect-error — payload satisfies the runtime shape but TS can't narrow the union
      previewMutation.mutate(payload);
    }, 300);

    return () => {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    };
  }, [form, combination, combinationId]);

  if (isComboLoading) {
    return (
      <div className="flex items-center justify-center p-8 text-fg-muted text-sm">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading configuration...
      </div>
    );
  }

  if (!combination) {
    return null;
  }

  const { inputBindings } = combination;
  const preview = previewMutation.data;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    for (const binding of inputBindings) {
      if (binding.isRequired) {
        const formKey = getFormKey(binding.code);
        if (!form[formKey]) return;
      }
    }

    onSubmit({
      combinationId,
      seriesId: combination.seriesId,
      typeId: combination.typeId,
      inputUnit: form.inputUnit || 'mm',
      inputWidthValue: parseFloat(form.widthMm || 0),
      inputHeightValue: parseFloat(form.heightMm || 0),
      g1Value: form.g1Value ? parseFloat(form.g1Value) : undefined,
      g2Value: form.g2Value ? parseFloat(form.g2Value) : undefined,
      widthMm: parseInt(form.widthMm || 0, 10),
      heightMm: parseInt(form.heightMm || 0, 10),
      qty: parseInt(form.qty || 1, 10),
      glassType: form.glassType || undefined,
      locationNotes: form.locationNotes || undefined,
      remarks: form.remarks || undefined,
    });
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Dimensions & Inputs</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {inputBindings.map((binding) => {
                const formKey = getFormKey(binding.code);
                const isNumeric = binding.dataType === 'numeric';
                return (
                  <div key={binding.id} className="space-y-1.5">
                    <Label className="text-xs">
                      {binding.labelOverride || binding.label}{binding.isRequired === 1 && <span className="text-destructive"> *</span>}
                    </Label>
                    <Input
                      type={isNumeric ? 'number' : 'text'}
                      value={form[formKey] || ''}
                      onChange={(e) => setField(formKey, e.target.value)}
                      className="h-9 text-sm"
                      {...(isNumeric ? { min: 0 } : {})}
                    />
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Remarks</Label>
                <Input
                  placeholder="Optional notes"
                  value={form.remarks || ''}
                  onChange={(e) => setField('remarks', e.target.value)}
                  className="h-9 text-sm"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-1">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving…' : submitLabel}
              </Button>
              <Button type="button" variant="ghost" onClick={onReset}>
                {isEdit ? 'Cancel' : 'Reset'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="h-fit">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <Eye className="h-4 w-4" />
            Live Preview
          </CardTitle>
        </CardHeader>
        <CardContent>
          {previewMutation.isPending && (
            <div className="flex items-center gap-2 text-xs text-fg-muted">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Calculating…
            </div>
          )}

          {!preview && !previewMutation.isPending && (
            <p className="text-xs text-fg-muted leading-relaxed">
              Enter dimensions to see dynamic calculations.
            </p>
          )}

          {preview && !previewMutation.isPending && (
            <div className="space-y-3 text-xs">
               <div className="grid gap-2">
                 {preview.outerFrameTopMm != null && (
                    <div className="flex justify-between items-center">
                      <span className="text-fg-secondary">Outer Frame T/B</span>
                      <span className="font-mono">{preview.outerFrameTopMm}mm × {preview.outerFrameQty}</span>
                    </div>
                 )}
                 {preview.outerFrameLeftMm != null && (
                    <div className="flex justify-between items-center">
                      <span className="text-fg-secondary">Outer Frame L/R</span>
                      <span className="font-mono">{preview.outerFrameLeftMm}mm × {preview.outerFrameQty}</span>
                    </div>
                 )}
                 {preview.fixedShutterMm != null && (
                    <div className="flex justify-between items-center">
                      <span className="text-fg-secondary">Fixed Shutter</span>
                      <span className="font-mono">{preview.fixedShutterMm}mm × {preview.fixedShutterQty}</span>
                    </div>
                 )}
                 {preview.openableShutterMm != null && (
                    <div className="flex justify-between items-center">
                      <span className="text-fg-secondary">Openable Shutter</span>
                      <span className="font-mono">{preview.openableShutterMm}mm × {preview.openableShutterQty}</span>
                    </div>
                 )}
                 {preview.glassWidthMm != null && (
                    <div className="flex justify-between items-center">
                      <span className="text-fg-secondary">Glass</span>
                      <span className="font-mono">{preview.glassWidthMm} × {preview.glassHeightMm}mm × {preview.glassQty}</span>
                    </div>
                 )}
               </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
